import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Building, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTimeAgo } from '../lib/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Local news categories for Rwanda
const LOCAL_CATEGORIES = [
  { id: 'all', name: 'All Local', icon: <MapPin className="h-4 w-4" /> },
  { id: 'kigali', name: 'Kigali', icon: <Building className="h-4 w-4" /> },
  { id: 'eastern', name: 'Eastern', icon: <Navigation className="h-4 w-4" /> },
  { id: 'western', name: 'Western', icon: <Navigation className="h-4 w-4" /> },
  { id: 'northern', name: 'Northern', icon: <Navigation className="h-4 w-4" /> },
  { id: 'southern', name: 'Southern', icon: <Navigation className="h-4 w-4" /> },
];

// Rwanda-specific keywords for region filtering
const REGION_KEYWORDS = {
  kigali: ['kigali', 'nyarugenge', 'gasabo', 'kicukiro'],
  eastern: ['eastern province', 'bugesera', 'gatsibo', 'kayonza', 'kirehe', 'ngoma', 'nyagatare', 'rwamagana'],
  western: ['western province', 'karongi', 'ngororero', 'nyabihu', 'nyamasheke', 'rubavu', 'rusizi', 'rutsiro', 'lake kivu'],
  northern: ['northern province', 'burera', 'gakenke', 'gicumbi', 'musanze', 'rulindo', 'volcanoes', 'gorilla'],
  southern: ['southern province', 'gisagara', 'huye', 'kamonyi', 'muhanga', 'nyamagabe', 'nyanza', 'nyaruguru', 'ruhango']
};

// Filter by region
const filterByRegion = (article, region) => {
  if (region === 'all') return true;
  const text = `${article.title || ''} ${article.title_rw || ''} ${article.description || ''}`.toLowerCase();
  const keywords = REGION_KEYWORDS[region] || [];
  return keywords.some(kw => text.includes(kw));
};

const LocalPage = ({ onArticleClick }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  // Fetch ONLY in-house/user-created articles
  useEffect(() => {
    const fetchLocalNews = async () => {
      setLoading(true);
      try {
        // Only fetch user-created in-house articles
        const response = await fetch(`${BACKEND_URL}/api/my-stories`);
        const data = await response.json();
        
        // Filter only in-house articles (is_user_created = true)
        const inHouseArticles = (data || []).filter(article => 
          article.is_user_created === true
        );
        
        // Apply region filter
        const filteredArticles = inHouseArticles.filter(article => 
          filterByRegion(article, selectedCategory)
        );
        
        // Sort by date (newest first)
        filteredArticles.sort((a, b) => {
          const dateA = new Date(a.published_at || 0);
          const dateB = new Date(b.published_at || 0);
          return dateB - dateA;
        });
        
        setArticles(filteredArticles);
      } catch (error) {
        console.error('Failed to fetch local news:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLocalNews();
  }, [selectedCategory]);

  // Get localized title
  const getTitle = (article) => {
    if (isKinyarwanda && article.title_rw) return article.title_rw;
    return article.title_en || article.title || 'Untitled';
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
            <MapPin className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isKinyarwanda ? 'Amakuru y\'Aho' : 'Local News'}
            </h1>
            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <Navigation className="h-3 w-3" />
              <span>Rwanda • In-House Content Only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        {LOCAL_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === cat.id
                ? 'bg-emerald-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {cat.icon}
            <span>{cat.name}</span>
          </button>
        ))}
      </div>

      {/* Info Banner */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-emerald-900 dark:text-emerald-200">
              {isKinyarwanda ? 'Amakuru y\'u Rwanda' : 'Rwanda Local News'}
            </h4>
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              {isKinyarwanda 
                ? 'Amakuru yanditswe n\'abakozi bacu gusa - nta makuru y\'inyuma.'
                : 'Only articles written by our team - no external feeds.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* News Grid - Smaller cards like homepage */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="animate-pulse bg-gray-100 dark:bg-gray-800 rounded-lg h-48" />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-2xl">
          <MapPin className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {isKinyarwanda 
              ? 'Nta makuru y\'aho ariho ubu' 
              : 'No local in-house articles available'
            }
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
            {isKinyarwanda
              ? 'Ongera ugerageze nyuma'
              : 'Create articles in Admin Panel to see them here'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {articles.map((article, idx) => (
            <div
              key={article.id || idx}
              onClick={() => onArticleClick?.(article)}
              data-testid={`local-article-${idx}`}
              className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
            >
              {/* Image - Smaller aspect ratio */}
              <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-800 relative overflow-hidden">
                <img
                  src={article.image_url || 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=400&h=300&fit=crop'}
                  alt={getTitle(article)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=400&h=300&fit=crop';
                  }}
                />
              </div>
              
              {/* Content - Compact */}
              <div className="p-3">
                <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 mb-1.5">
                  <span className="font-medium truncate">{article.source || 'YEGO'}</span>
                  <span>•</span>
                  <span className="flex items-center gap-0.5">
                    <Clock className="h-2.5 w-2.5" />
                    {getTimeAgo(article.published_at || article.created_at || article.date)}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors leading-snug">
                  {getTitle(article)}
                </h3>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalPage;
